import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/userSlice";
import Swal from "sweetalert2";
import toast, { Toaster } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartShopping,
  faTag,
  faCreditCard,
  faBolt,
  faRightToBracket,
  faUserPlus,
  faShieldHalved,
  faHeart,
  faBoxOpen,
  faPowerOff,
  faRotateRight,
  faCheck,
  faFloppyDisk,
  faSpinner,
  faSliders,
  faStore,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import EcommerceSettingsService from "../../services/ecommerceSettings.service";
import { getUserRoles, hasRole } from "../../utils/roles";

const initialDefaultSettings = {
  ecommerce_active: true,
  show_prices: true,
  show_cart: true,
  show_checkout: true,
  show_buy_now: true,
  show_login: true,
  show_register: true,
  show_auth_pages: true,
  show_wishlist: true,
  show_orders: true,
};

function EcommerceSettings({ isStandalone = false }) {
  const currentUser = useSelector(selectUser);
  const adminRole = getUserRoles(currentUser);
  const canEdit = hasRole(adminRole, ["Admin"]) || adminRole.length === 0;

  const [settings, setSettings] = useState(initialDefaultSettings);
  const [originalSettings, setOriginalSettings] = useState(initialDefaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await EcommerceSettingsService.getSettings();
      if (data) {
        const loaded = {
          ecommerce_active: Boolean(data.ecommerce_active ?? true),
          show_prices: Boolean(data.show_prices ?? true),
          show_cart: Boolean(data.show_cart ?? true),
          show_checkout: Boolean(data.show_checkout ?? true),
          show_buy_now: Boolean(data.show_buy_now ?? true),
          show_login: Boolean(data.show_login ?? true),
          show_register: Boolean(data.show_register ?? true),
          show_auth_pages: Boolean(data.show_auth_pages ?? true),
          show_wishlist: Boolean(data.show_wishlist ?? true),
          show_orders: Boolean(data.show_orders ?? true),
        };
        setSettings(loaded);
        setOriginalSettings(loaded);
        if (data.updatedAt) {
          setLastUpdated(data.updatedAt);
        }
      }
    } catch (error) {
      console.error("Error fetching ecommerce settings:", error);
      toast.error("Could not load current settings. Default values displayed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleToggle = (key) => {
    if (!canEdit) {
      toast.error("You don't have permission to modify ecommerce settings.");
      return;
    }
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const applyPreset = (presetType) => {
    if (!canEdit) return;

    if (presetType === "enable_all") {
      setSettings({
        ecommerce_active: true,
        show_prices: true,
        show_cart: true,
        show_checkout: true,
        show_buy_now: true,
        show_login: true,
        show_register: true,
        show_auth_pages: true,
        show_wishlist: true,
        show_orders: true,
      });
      toast.success("All features set to Enabled (Click Save to apply)");
    } else if (presetType === "disable_all") {
      setSettings({
        ecommerce_active: false,
        show_prices: false,
        show_cart: false,
        show_checkout: false,
        show_buy_now: false,
        show_login: false,
        show_register: false,
        show_auth_pages: false,
        show_wishlist: false,
        show_orders: false,
      });
      toast("All features set to Disabled (Click Save to apply)", { icon: "⚠️" });
    } else if (presetType === "catalog_mode") {
      // Catalog only: Prices and Auth visible, but buying/cart disabled
      setSettings((prev) => ({
        ...prev,
        ecommerce_active: false,
        show_prices: true,
        show_cart: false,
        show_checkout: false,
        show_buy_now: false,
        show_orders: false,
        show_wishlist: true,
      }));
      toast.success("Catalog Showcase Mode selected (Click Save to apply)");
    } else if (presetType === "reset") {
      setSettings(originalSettings);
      toast("Reset back to server settings", { icon: "🔄" });
    }
  };

  const isDirty = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!canEdit) {
      toast.error("Permission denied. Only Admins can save changes.");
      return;
    }

    setSaving(true);
    try {
      const updated = await EcommerceSettingsService.updateSettings(settings, currentUser);
      setOriginalSettings(settings);
      if (updated?.updatedAt) {
        setLastUpdated(updated.updatedAt);
      } else {
        setLastUpdated(new Date().toISOString());
      }

      Swal.fire({
        icon: "success",
        title: "Settings Saved Successfully!",
        text: "The website ecommerce settings have been updated in real-time.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update ecommerce settings:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Save",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Please verify your connection and permissions.",
      });
    } finally {
      setSaving(false);
    }
  };

  const masterControls = [
    {
      key: "ecommerce_active",
      title: "E-Commerce Master Switch",
      arTitle: "المفتاح الرئيسي للمتجر والبيع الإلكتروني",
      desc: "Master switch to enable or disable all shopping & e-commerce features storewide.",
      arDesc: "ماستر سويتش لتعطيل أو تفعيل كامل خواص المتجر والـ Ecommerce دفعة واحدة",
      icon: faPowerOff,
      accentColor: "from-blue-600 to-sky-600",
      highlight: true,
    },
    {
      key: "show_auth_pages",
      title: "Authentication Pages Master",
      arTitle: "التحكم العام في صفحات الدخول والتسجيل",
      desc: "Global master control for all customer login and registration pages & routes.",
      arDesc: "تحكم عام في كل صفحات تسجيل الدخول وإنشاء الحساب",
      icon: faShieldHalved,
      accentColor: "from-indigo-600 to-purple-600",
      highlight: true,
    },
  ];

  const shoppingControls = [
    {
      key: "show_prices",
      title: "Product Prices",
      arTitle: "أسعار المنتجات",
      desc: "Show or hide product prices for all items across the storefront.",
      arDesc: "إظهار أو إخفاء أسعار المنتجات في الموقع",
      icon: faTag,
      disabledWhen: !settings.ecommerce_active,
    },
    {
      key: "show_cart",
      title: "Shopping Cart",
      arTitle: "سلة المشتريات (Cart)",
      desc: "Show or hide the shopping cart icon, drawer, and cart page.",
      arDesc: "إظهار أو إخفاء سلة الشراء وسلة المشتريات",
      icon: faCartShopping,
      disabledWhen: !settings.ecommerce_active,
    },
    {
      key: "show_checkout",
      title: "Checkout & Ordering Page",
      arTitle: "صفحة إتمام الطلب (Checkout)",
      desc: "Enable or disable customer checkout, payment, and ordering flow.",
      arDesc: "إظهار أو إخفاء صفحة إتمام الطلب والبيع",
      icon: faCreditCard,
      disabledWhen: !settings.ecommerce_active,
    },
    {
      key: "show_buy_now",
      title: "Quick 'Buy Now' Button",
      arTitle: "زر اشتري الآن السريع",
      desc: "Display or hide direct 'Buy Now' one-click checkout buttons on product cards.",
      arDesc: "إظهار أو إخفاء زر 'اشتري الآن' السريع في كروت وتفاصيل المنتجات",
      icon: faBolt,
      disabledWhen: !settings.ecommerce_active,
    },
    {
      key: "show_wishlist",
      title: "Wishlist & Favorites",
      arTitle: "قائمة الرغبات والمفضلة",
      desc: "Allow customers to add products to their favorites/wishlist.",
      arDesc: "إظهار أو إخفاء قائمة الرغبات / المفضلة",
      icon: faHeart,
    },
    {
      key: "show_orders",
      title: "Orders & Order History",
      arTitle: "صفحة طلباتي وسجل الطلبات",
      desc: "Show or hide customer order tracking and purchase history.",
      arDesc: "إظهار أو إخفاء صفحة طلباتي وسجل الطلبات للمستخدمين",
      icon: faBoxOpen,
    },
  ];

  const authControls = [
    {
      key: "show_login",
      title: "Customer Login",
      arTitle: "تسجيل الدخول",
      desc: "Show or hide login button and customer sign-in portal.",
      arDesc: "إظهار أو إخفاء زر وصفحة تسجيل الدخول",
      icon: faRightToBracket,
      disabledWhen: !settings.show_auth_pages,
    },
    {
      key: "show_register",
      title: "Customer Registration",
      arTitle: "إنشاء حساب جديد",
      desc: "Allow new visitors to register a new account on the store.",
      arDesc: "إظهار أو إخفاء زر وصفحة إنشاء حساب جديد",
      icon: faUserPlus,
      disabledWhen: !settings.show_auth_pages,
    },
  ];

  const renderCard = (ctrl) => {
    const isChecked = Boolean(settings[ctrl.key]);
    const isHighlighted = ctrl.highlight;

    return (
      <div
        key={ctrl.key}
        className={`relative flex flex-col justify-between p-4 rounded-xl border transition-all duration-200 ${
          isChecked
            ? isHighlighted
              ? "bg-gradient-to-r from-sky-50 to-blue-50 border-[#0388CC] shadow-sm ring-1 ring-[#0388CC]/30"
              : "bg-white border-[#0388CC]/40 shadow-sm hover:border-[#0388CC]"
            : "bg-slate-50/70 border-slate-200 opacity-90 hover:opacity-100"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-white ${
                isChecked
                  ? isHighlighted
                    ? "bg-gradient-to-br from-[#0388CC] to-[#005f94] shadow-md shadow-[#0388CC]/20"
                    : "bg-[#0388CC]"
                  : "bg-slate-400"
              }`}
            >
              <FontAwesomeIcon icon={ctrl.icon} className="text-[16px]" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-[15px] text-[#033362]">
                  {ctrl.title}
                </span>
                <span className="text-[12px] font-semibold text-slate-500 font-arabic">
                  ({ctrl.arTitle})
                </span>
              </div>
              <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">
                {ctrl.desc}
              </p>
              <p className="text-[11px] text-slate-500 font-arabic mt-0.5" dir="rtl">
                {ctrl.arDesc}
              </p>
            </div>
          </div>

          {/* Toggle Switch */}
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => handleToggle(ctrl.key)}
            className={`relative shrink-0 inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#0388CC] focus:ring-offset-2 ${
              isChecked ? "bg-[#007530]" : "bg-slate-300"
            } ${!canEdit ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            aria-pressed={isChecked}
            title={isChecked ? "Click to Disable" : "Click to Enable"}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                isChecked ? "translate-x-8" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* Status Pill & Key */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 text-[11px]">
          <span className="font-mono text-slate-400">{ctrl.key}</span>
          <span
            className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider text-[10px] ${
              isChecked
                ? "bg-green-100 text-[#007530]"
                : "bg-slate-200 text-slate-600"
            }`}
          >
            {isChecked ? "Active / مفعل" : "Disabled / معطل"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={`w-full ${isStandalone ? "p-6" : "p-4"}`}>
      <Toaster position="top-right" />

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#033362] via-[#0388CC] to-[#00AEEF] text-white p-6 rounded-2xl shadow-lg mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/15 backdrop-blur-md rounded-xl">
              <FontAwesomeIcon icon={faStore} className="text-[24px] text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                E-Commerce & Storefront Controls
              </h1>
              <p className="text-[13px] text-blue-100 mt-0.5 font-arabic">
                التحكم الشامل في بوابات الشراء، السلة، الأسعار، وحسابات العملاء بالموقع
              </p>
            </div>
          </div>
          {lastUpdated && (
            <p className="text-[11px] text-blue-200 mt-3">
              Last synced: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => fetchSettings()}
            disabled={loading || saving}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3.5 py-2 rounded-xl text-[13px] font-semibold transition border border-white/20 disabled:opacity-50"
            title="Reload from API"
          >
            <FontAwesomeIcon icon={faRotateRight} className={loading ? "animate-spin" : ""} />
            Sync
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || !canEdit || !isDirty}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[14px] font-bold shadow-md transition ${
              isDirty
                ? "bg-amber-400 hover:bg-amber-300 text-slate-900 animate-pulse ring-2 ring-amber-200"
                : "bg-white hover:bg-slate-100 text-[#033362]"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFloppyDisk} />
                {isDirty ? "Save Changes (Unsaved)" : "Save Settings"}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preset Quick Actions */}
      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] text-slate-600 font-semibold">
          <FontAwesomeIcon icon={faSliders} className="text-[#0388CC]" />
          <span>Quick Presets:</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => applyPreset("enable_all")}
            disabled={!canEdit}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition"
          >
            Enable All Features (تفعيل الكل)
          </button>
          <button
            type="button"
            onClick={() => applyPreset("catalog_mode")}
            disabled={!canEdit}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition"
          >
            Catalog Mode Only (كتالوج وعرض فقط)
          </button>
          <button
            type="button"
            onClick={() => applyPreset("disable_all")}
            disabled={!canEdit}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition"
          >
            Disable All (تعطيل كامل)
          </button>
          {isDirty && (
            <button
              type="button"
              onClick={() => applyPreset("reset")}
              className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300 transition"
            >
              Reset to Server
            </button>
          )}
        </div>
      </div>

      {/* Notice if Master switch is OFF */}
      {!settings.ecommerce_active && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-3">
          <FontAwesomeIcon icon={faCircleExclamation} className="text-amber-600 text-lg mt-0.5" />
          <div className="text-[13px]">
            <p className="font-bold">E-Commerce Master Switch is currently DISABLED</p>
            <p className="text-amber-800 text-[12px] mt-0.5 font-arabic">
              تم تعطيل المتجر الإلكتروني بشكل عام. حتى وإن كانت أزرار السلة أو الطلب مفعلة أدناه، فلن تظهر للزوار حتى يتم تفعيل المفتاح الرئيسي للمتجر.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center p-16 text-slate-400">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-[#0388CC] mb-3" />
          <p className="font-semibold text-[14px]">Loading settings from server...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Section 1: Master Controls */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-6 bg-[#0388CC] rounded-full inline-block"></span>
              <h2 className="text-[16px] font-bold text-[#033362]">
                1. Master Switches (التحكم الرئيسي الشامل)
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {masterControls.map(renderCard)}
            </div>
          </div>

          {/* Section 2: Storefront & Shopping */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-6 bg-[#007530] rounded-full inline-block"></span>
              <h2 className="text-[16px] font-bold text-[#033362]">
                2. Storefront, Shopping & Orders (المتجر والسلة والطلبات)
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shoppingControls.map(renderCard)}
            </div>
          </div>

          {/* Section 3: Authentication & Accounts */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-6 bg-purple-600 rounded-full inline-block"></span>
              <h2 className="text-[16px] font-bold text-[#033362]">
                3. Customer Accounts & Registration (حسابات العملاء والدخول)
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {authControls.map(renderCard)}
            </div>
          </div>

          {/* Bottom Save Bar (Floating or bottom pinned if dirty) */}
          <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-[12px] text-slate-500 font-medium">
              {isDirty ? (
                <span className="text-amber-600 font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping"></span>
                  You have unsaved changes. Don't forget to click Save!
                </span>
              ) : (
                <span>All settings are currently in sync with live server.</span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {isDirty && (
                <button
                  type="button"
                  onClick={() => applyPreset("reset")}
                  className="px-4 py-2 rounded-xl text-[13px] font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Discard Changes
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !canEdit || !isDirty}
                className="flex items-center gap-2 bg-[#007530] hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-bold text-[14px] shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    Save Ecommerce Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EcommerceSettings;
